import { PageAction, PageName, EventParams, ContentSource } from '@mecontrol/common';
import { SyntheticEvent, getPerfEntries, perfNow, getOptions, ME } from './utilities';
import { setUpTelemetry, fireEvent, getCurrentAccountType } from './telemetry';
import { AccountType } from '@mecontrol/public-api';

setUpTelemetry(!!(ME.Config && ME.Config.main !== "iframeBootCore"));

/**
 * Logs a telemetry event. Uses a proxy to allow queueing of events until the Event Generator is ready
 * @param type A string containing the type of event we are logging
 * @param eventParams An object that contains all the information needed to create the event
 */
export function logTelemetryEvent(eventParams: EventParams): void {
    if (!ME?.Config?.enableTelemetry || 
        (!ME?.Config?.enableAADTelemetry && (getCurrentAccountType() === AccountType.AAD || getCurrentAccountType() === undefined))) {
        return;
    }

    const isInitialView = (params: EventParams) =>
        params.eventType === 'ContentUpdate' &&
        params.content.source === ContentSource.UX &&
        params.content.id === PageName.initial;
    const isInitialPageAction = (params: EventParams) =>
        params.eventType === 'PageAction' &&
        params.content &&
        (params.content.id == PageAction.signOut || params.content.id == PageAction.viewAccount);

    if (isInitialView(eventParams) || isInitialPageAction(eventParams)) {
        const options = getOptions();
        if (options) {
            let syntheticEvent = new SyntheticEvent("telemetry", {
                data: {
                    event: eventParams
                }
            });
            options.syntheticEventTarget.dispatchEvent(syntheticEvent); // no need to catch the return since it's not cancellable
        }
    }

    fireEvent(eventParams);
}

/**
 * Use the performance API to fire OutgoingRequest telemetry events for resources that
 * belong to us but whose download was not initiated by us (thus making us incapable of
 * tracking them)
 */
export function logResourcesDownloaded(): void {
    let now = perfNow();

    // Gather all performance entries and filter them out
    // using the regex above
    getPerfEntries()
        .filter((entry: PerformanceEntry) => isMeControlUrl(entry.name))

        // Queue up telemetry for entries that may belong to us.
        .forEach((entry: PerformanceEntry) => logTelemetryEvent({
            eventType: 'OutgoingRequest',
            serviceOperation: 'LoadResource',
            service: 'MeControl',
            url: entry.name,
            success: true,
            duration: now - entry.startTime
        }));
}

// Regex used for testing against URL domains for our possible MeControl domains:
// - mem.gfx.ms
// - controls.account.microsoft.com
// - controls.account.microsoft-int.com
// - controls.account.microsoft-dev.com
// - amcdn.msauth.net
// - amcdn.msftauth.net
const domainRegex = /^(?:mem\.gfx\.ms|controls\.account\.microsoft|amcdn\.ms(?:ft)?auth\.net)/i;

/**
 * Determine whether a given URL is for a MeControl resource/endpoint
 * @param url URL to check
 */
function isMeControlUrl(url: string): boolean {
    const a = document.createElement('a');
    a.href = url;
    const domain = a.hostname;

    return domainRegex.test(domain);
}
